import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AddUser: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    employee_id: '',
    password: '',
    confirm_password: '',
    role: 'user',
    status: 'active',
    phone: '',
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id.replace(/-/g, '_')]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const data = new FormData();

      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('password', formData.password);
      data.append('role', formData.role);
      data.append('employee_id', formData.employee_id);
      data.append('phone', formData.phone);
      data.append('is_active', String(formData.status === 'active'));

      if (profileImage) {
        data.append('profileImage', profileImage);
      }

      const response = await fetch(`${baseUrl}/api/v1/users`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: data,
        // Do NOT set Content-Type header when using FormData, browser does it automatically with boundary
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to create user');
      }

      navigate('/user-management');
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating the user.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full animate-in fade-in duration-500">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1 font-medium">
          <Link to="/user-management" className="hover:text-[#136bce]">User Management</Link>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#136bce]">Add New User</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Add New User</h1>
        <p className="text-sm text-slate-500">Enter the details below to create a new user account.</p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200 flex items-center gap-3 text-red-700 animate-in slide-in-from-top-2">
          <span className="material-symbols-outlined text-[20px]">error</span>
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <div className="mx-auto max-w-4xl pb-12">
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <form onSubmit={handleSubmit}>
            {/* Profile Image Upload */}
            <div className="mb-8 flex flex-col items-center">
              <div className="relative mb-4 group">
                <div className="h-24 w-24 overflow-hidden rounded-full bg-slate-100 ring-4 ring-slate-50 shadow-sm border border-slate-200 flex items-center justify-center">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="h-full w-full object-cover" />
                  ) : (
                    <span className="material-symbols-outlined text-[40px] text-slate-300">person</span>
                  )}
                </div>
                <label
                  htmlFor="profile-image-upload"
                  className="absolute bottom-0 right-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-[#136bce] text-white shadow-lg transition-transform hover:scale-110 active:scale-95"
                >
                  <span className="material-symbols-outlined text-[18px]">photo_camera</span>
                  <input
                    type="file"
                    id="profile-image-upload"
                    className="hidden"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </label>
              </div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Profile Photo</p>
            </div>

            <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="name">Full Name</label>
                <input
                  className="w-full rounded-lg border-slate-200 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                  id="name"
                  placeholder="e.g. Michael Chang"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="employee-id">Employee ID</label>
                <input
                  className="w-full rounded-lg border-slate-200 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                  id="employee-id"
                  placeholder="e.g. EMP-2023-001"
                  type="text"
                  required
                  value={formData.employee_id}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">Email ID</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="material-symbols-outlined text-[18px] text-slate-400">mail</span>
                  </div>
                  <input
                    className="w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                    id="email"
                    placeholder="e.g. michael.chang@qamate.com"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="phone">Phone Number</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="material-symbols-outlined text-[18px] text-slate-400">phone</span>
                  </div>
                  <input
                    className="w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                    id="phone"
                    placeholder="e.g. +91 98765 43210"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="password">Password</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="material-symbols-outlined text-[18px] text-slate-400">lock</span>
                  </div>
                  <input
                    className="w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                    id="password"
                    placeholder="••••••••"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="confirm-password">Confirm Password</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="material-symbols-outlined text-[18px] text-slate-400">lock_reset</span>
                  </div>
                  <input
                    className="w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                    id="confirm-password"
                    placeholder="••••••••"
                    type="password"
                    required
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="role">User Role</label>
                <select
                  className="w-full rounded-lg border-slate-200 py-2.5 text-sm text-slate-700 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow bg-white"
                  id="role"
                  required
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="user">QA Inspector</option>
                  <option value="Senior Inspector">Senior Inspector</option>
                  <option value="Inspector">Inspector</option>
                  <option value="Manager">QA Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="status">Status</label>
                <select
                  className="w-full rounded-lg border-slate-200 py-2.5 text-sm text-slate-700 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow bg-white"
                  id="status"
                  required
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </div>
            <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-100 pt-6">
              <button
                type="button"
                onClick={() => navigate('/user-management')}
                className="rounded-lg border border-slate-200 bg-white px-6 py-2.5 text-sm font-bold text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900 focus:outline-none"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-md shadow-blue-500/20 transition-all hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98] focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[18px]">save</span>
                    Save User
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
